// Quyết toán idle closed-form (CULTIVATION §1.3, BALANCE §2.3). Hàm thuần: cùng input → cùng output.
//  - Quãng vắng bị cắt ở OFFLINE_CAP_HOURS TRƯỚC khi nhân hệ số; phần bị cắt báo trong cappedMs.
//  - Chia khối OFFLINE_BLOCK_HOURS; qiRate tính ở đầu khối (đột phá giữa khối đổi tốc độ ở khối sau).
//  - Tử Quan ×3 tính từng đoạn trong khối; hết hạn giữa chừng thì phần còn lại về Tiểu Bế Quan.
//  - Mỗi khối offline rút đúng 1 số RNG (log hiếm), seed = hash(seed, 'idle', mốc đầu khối).
//  - Idle KHÔNG BAO GIỜ đổi tuổi / thọ nguyên (Bất biến 4).
import { BALANCE } from '@data/balance';
import { IDLE_RARE_LINE_KEYS } from '@data/content/idleLines';
import { createRng, deriveSeed } from '../core/rng';
import { HOUR_MS } from '../core/time';
import { grantQi, type MinorBreakthrough } from './grant';
import { efficiency, masteryMult, qiRate } from './rate';
import { decayToxicity } from '../crafting';
import type { CultivationState } from './types';

const WEEK_S = 7 * 24 * 3600;

export interface IdleSettleReport {
  fromMs: number;
  toMs: number;
  online: boolean;
  /** Phần vắng bị cắt vì vượt trần (ms). */
  cappedMs: number;
  qiGained: number;
  overflowGained: number;
  wastedQi: number;
  masteryGained: number;
  minorBreakthroughs: MinorBreakthrough[];
  /** Khóa i18n tin Thế Sự. */
  worldNews: string[];
  /** Khóa i18n log idle hiếm. */
  rareLines: string[];
  /** Mốc Tử Quan kết thúc trong quãng này (xuất quan), hoặc null. */
  deathSeclusionEndedAtMs: number | null;
}

export interface SettleResult {
  state: CultivationState;
  report: IdleSettleReport;
}

export function emptyReport(fromMs: number, toMs: number, online: boolean): IdleSettleReport {
  return {
    fromMs,
    toMs,
    online,
    cappedMs: 0,
    qiGained: 0,
    overflowGained: 0,
    wastedQi: 0,
    masteryGained: 0,
    minorBreakthroughs: [],
    worldNews: [],
    rareLines: [],
    deathSeclusionEndedAtMs: null,
  };
}

function endDeathSeclusion(state: CultivationState, atMs: number): CultivationState {
  return {
    ...state,
    idle: {
      ...state.idle,
      seclusion: { kind: 'light', startedAtMs: atMs, endsAtMs: null, lifespanPrepaid: 0 },
    },
  };
}

export function settleIdle(
  input: CultivationState,
  fromMs: number,
  toMs: number,
  seed: number,
  online: boolean,
): SettleResult {
  const report = emptyReport(fromMs, toMs, online);
  let state = input;
  const elapsed = Math.max(0, toMs - fromMs);
  const effective = Math.min(elapsed, BALANCE.OFFLINE_CAP_HOURS * HOUR_MS);
  report.cappedMs = elapsed - effective;
  const stopMs = fromMs + effective;
  const blockMs = BALANCE.OFFLINE_BLOCK_HOURS * HOUR_MS;

  for (let t = fromMs; t < stopMs;) {
    const end = Math.min(t + blockMs, stopMs);
    const durS = (end - t) / 1000;
    const rate = qiRate(state, t);
    const eff = efficiency(state, online);
    const sec = state.idle.seclusion;
    let deathS = 0;
    if (sec.kind === 'death' && sec.endsAtMs !== null)
      deathS = Math.max(0, Math.min(end, sec.endsAtMs) - t) / 1000;
    const trial = state.idle.mode === 'trial';
    const share = trial ? BALANCE.TRIAL_QI_SHARE : 1;
    const qi = rate * eff * share * (deathS * BALANCE.DEATH_SECLUSION_MULT + (durS - deathS));
    const mastery =
      (BALANCE.MASTERY_PER_MIN / 60) *
      durS *
      eff *
      masteryMult(state) *
      (trial ? BALANCE.MASTERY_TRIAL_SHARE : 1);

    const g = grantQi(state, qi, end);
    state = {
      ...g.state,
      technique: { ...g.state.technique, mastery: g.state.technique.mastery + mastery },
      pillToxicity: decayToxicity(g.state.pillToxicity, durS / 3600, trial),
    };
    report.qiGained += qi;
    report.overflowGained += g.overflowGained;
    report.wastedQi += g.wasted;
    report.masteryGained += mastery;
    report.minorBreakthroughs.push(...g.breakthroughs);
    report.worldNews.push(...g.news);

    if (sec.kind === 'death' && sec.endsAtMs !== null && sec.endsAtMs <= end) {
      state = endDeathSeclusion(state, sec.endsAtMs);
      report.deathSeclusionEndedAtMs = sec.endsAtMs;
    }

    if (!online && IDLE_RARE_LINE_KEYS.length > 0) {
      const rng = createRng(deriveSeed(seed, 'idle', t));
      const p = (durS / WEEK_S) * BALANCE.IDLE_RARE_LINES_PER_WEEK;
      const roll = rng.next();
      if (roll < p)
        report.rareLines.push(IDLE_RARE_LINE_KEYS[Math.floor((roll / p) * IDLE_RARE_LINE_KEYS.length)]!);
    }
    t = end;
  }

  // Tử Quan hết hạn trong phần bị cắt vì trần: vẫn xuất quan (thời gian thật đã qua).
  const sec = state.idle.seclusion;
  if (sec.kind === 'death' && sec.endsAtMs !== null && sec.endsAtMs <= toMs) {
    state = endDeathSeclusion(state, sec.endsAtMs);
    report.deathSeclusionEndedAtMs = sec.endsAtMs;
  }
  state = { ...state, idle: { ...state.idle, lastSettledMs: Math.max(state.idle.lastSettledMs, toMs) } };
  return { state, report };
}
